import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ShoppingCart,
  RefreshCw,
  Building2,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  Boxes,
  PackagePlus,
  Coins,
  FileText,
} from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatNumber, formatMoney } from "@/lib/utils";
import EntityLink, { EntityLinkContext } from "@/components/shared/EntityLink";
import { useVendorLookup } from "@/hooks/useFKLookup";
import { useHeaderSearch } from "@/hooks/useHeaderSearch";

export function MrpShortageQueueTab() {
  const navigate = useNavigate();
  const { headerSearch } = useHeaderSearch();
  const { options: vendorOptions } = useVendorLookup();
  const [selectedVendorId, setSelectedVendorId] = useState<string>("ALL");
  const [selectedPlanIds, setSelectedPlanIds] = useState<Set<string>>(new Set());

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: [
      "mrp-shortage-queue",
      headerSearch,
      selectedVendorId === "ALL" ? undefined : selectedVendorId,
    ],
    queryFn: async () => {
      return await trpcClient.purchaseOrders.getMrpShortageQueue.query({
        search: headerSearch.trim() || undefined,
        vendorId: selectedVendorId === "ALL" ? undefined : selectedVendorId,
      });
    },
  });

  const items = data?.items || [];
  const metrics = data?.metrics || {
    totalItems: 0,
    totalShortageQty: 0,
    totalEstimatedAmount: 0,
    uniqueVendorsCount: 0,
    uniqueProductionOrdersCount: 0,
  };

  const toggleSelectPlan = (id: string) => {
    setSelectedPlanIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedPlanIds.size === items.length) {
      setSelectedPlanIds(new Set());
    } else {
      setSelectedPlanIds(new Set(items.map((it: any) => it.id)));
    }
  };

  // Check vendor homogeneity among selected items
  const selectedItems = useMemo(() => {
    return items.filter((it: any) => selectedPlanIds.has(it.id));
  }, [items, selectedPlanIds]);

  const selectedVendorMismatch = useMemo(() => {
    if (selectedItems.length <= 1) return false;
    const firstVendor = selectedItems[0]?.vendorId;
    return selectedItems.some((it: any) => it.vendorId !== firstVendor);
  }, [selectedItems]);

  const targetVendor = selectedItems[0]?.vendorName || "Nhà cung cấp";
  const targetVendorId = selectedItems[0]?.vendorId || "";

  // 1-Click Navigate to Canonical PurchasingForm
  const handleCreatePoSingle = (item: any) => {
    navigate({
      to: "/purchasing/create",
      search: {
        vendorId: item.vendorId || undefined,
        materialId: item.materialId || undefined,
        quantity: item.shortageQty || undefined,
        productionOrderId: item.productionOrderId || undefined,
        materialPlanId: item.id,
      },
    });
  };

  const handleCreatePoBulk = () => {
    if (selectedItems.length === 0) return;
    const planIds = selectedItems.map((it: any) => it.id).join(",");
    navigate({
      to: "/purchasing/create",
      search: {
        vendorId: targetVendorId || undefined,
        // Mỗi dòng kế hoạch thành 1 dòng đơn mua (trang tạo đơn tự nạp vật tư + SL cần mua của từng dòng)
        materialPlanIds: planIds,
      },
    });
  };

  // ListPageShell (cha) mặc định tắt EntityLink; tab này không có row-click nên bật lại để bấm được mã vật tư/LSX/NCC
  return (
    <EntityLinkContext.Provider value={{ disableLink: false }}>
    <div className="flex flex-col h-full space-y-2">
      {/* 1. Single-Row Inline Metrics Bar (36-38px) */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-gradient-to-r from-amber-50/80 via-white to-slate-50 border border-amber-200/80 rounded-md text-xs shadow-xs">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 font-medium text-amber-900">
            <ShoppingCart className="w-3.5 h-3.5 text-amber-600" />
            <span>Nhu cầu NVL cần mua:</span>
            <span className="font-bold text-amber-700 bg-amber-100/80 px-1.5 py-0.5 rounded-full text-[11px] tabular-nums">
              {formatNumber(metrics.totalItems)} dòng
            </span>
          </div>

          <div className="h-3.5 w-px bg-amber-200 hidden sm:block" />

          <div className="flex items-center gap-1 text-slate-600">
            <Boxes className="w-3.5 h-3.5 text-slate-500" />
            <span>Tổng lượng thiếu hụt:</span>
            <span className="font-bold text-slate-900 tabular-nums">
              {formatNumber(metrics.totalShortageQty)}
            </span>
          </div>

          <div className="h-3.5 w-px bg-amber-200 hidden md:block" />

          <div className="flex items-center gap-1 text-slate-600">
            <Coins className="w-3.5 h-3.5 text-emerald-600" />
            <span>Dự toán kinh phí:</span>
            <span className="font-bold text-emerald-700 tabular-nums">
              {formatMoney(metrics.totalEstimatedAmount)}
            </span>
          </div>

          <div className="h-3.5 w-px bg-amber-200 hidden lg:block" />

          <div className="flex items-center gap-1 text-slate-600">
            <Building2 className="w-3.5 h-3.5 text-indigo-500" />
            <span>NCC liên quan:</span>
            <span className="font-semibold text-indigo-700 tabular-nums">
              {formatNumber(metrics.uniqueVendorsCount)} NCC
            </span>
          </div>

          <div className="h-3.5 w-px bg-amber-200 hidden lg:block" />

          <div className="flex items-center gap-1 text-slate-600">
            <FileText className="w-3.5 h-3.5 text-blue-500" />
            <span>Lệnh SX F0:</span>
            <span className="font-semibold text-blue-700 tabular-nums">
              {formatNumber(metrics.uniqueProductionOrdersCount)} LSX
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="h-6 px-2 text-[11px] text-slate-600 hover:text-slate-900"
          >
            <RefreshCw
              className={`w-3 h-3 mr-1 ${isRefetching ? "animate-spin" : ""}`}
            />
            Làm mới
          </Button>
        </div>
      </div>

      {/* 2. Filter & Bulk Action Ribbon */}
      <div className="flex items-center justify-between gap-2 bg-white px-3 py-1.5 rounded-md border border-slate-200 text-xs">
        <div className="flex flex-wrap items-center gap-2.5">
          <label className="text-xs font-medium text-slate-600">Nhà cung cấp:</label>
          <select
            value={selectedVendorId}
            onChange={(e) => setSelectedVendorId(e.target.value)}
            className="h-7 px-2 text-xs border border-slate-200 rounded-md bg-slate-50 text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="ALL">Tất cả Nhà Cung Cấp ({vendorOptions.length})</option>
            {vendorOptions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>

          {headerSearch && (
            <span className="text-xs text-slate-500 italic bg-slate-50 px-2 py-0.5 rounded border border-slate-200">
              Từ khóa tìm kiếm: "{headerSearch}"
            </span>
          )}
        </div>

        {/* Bulk Action Button */}
        {selectedItems.length > 0 && (
          <div className="flex items-center gap-2 animate-in fade-in duration-200">
            {selectedVendorMismatch ? (
              <div className="flex items-center gap-1.5 text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200 text-[11px]">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                <span>
                  Các dòng chọn từ nhiều NCC khác nhau. Vui lòng chọn cùng NCC để gộp đơn.
                </span>
              </div>
            ) : (
              <Button
                size="sm"
                onClick={handleCreatePoBulk}
                className="h-7 px-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-xs shadow-xs"
              >
                <PackagePlus className="w-3.5 h-3.5 mr-1.5" />
                <span>
                  Lập Đơn Mua Hàng ({selectedItems.length} vật tư - {targetVendor})
                </span>
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* 3. High-Density Enterprise Data-Grid */}
      <div className="border border-slate-200 rounded-md bg-white overflow-hidden shadow-xs flex-1 flex flex-col min-h-0">
        <div className="overflow-auto flex-1 max-h-[calc(100vh-270px)]">
          <Table className="w-full text-xs caption-bottom apv-compact-table min-w-[1100px]">
            <TableHeader className="bg-slate-100/90 sticky top-0 z-20 border-b border-slate-200 text-slate-700">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10 px-2 py-2 text-center sticky left-0 z-30 bg-slate-100 !bg-white">
                  <Checkbox
                    checked={
                      items.length > 0 && selectedPlanIds.size === items.length
                    }
                    onCheckedChange={toggleSelectAll}
                    aria-label="Chọn tất cả"
                  />
                </TableHead>
                <TableHead className="w-[130px] font-semibold text-slate-900 sticky left-10 z-30 bg-slate-100 !bg-white border-r border-slate-200">
                  Mã Vật Tư
                </TableHead>
                <TableHead className="font-semibold text-slate-800 min-w-[200px]">
                  Tên Vật Tư & Quy Cách
                </TableHead>
                <TableHead className="w-[130px] font-semibold text-slate-800">
                  Lệnh SX F0
                </TableHead>
                <TableHead className="w-[100px] font-semibold text-slate-800 text-right">
                  Tồn Hiện Tại
                </TableHead>
                <TableHead className="w-[100px] font-semibold text-slate-800 text-right">
                  Nhu Cầu Kế Hoạch
                </TableHead>
                <TableHead className="w-[100px] font-semibold text-slate-800 text-right">
                  Đã Đặt Mua
                </TableHead>
                <TableHead className="w-[110px] font-semibold text-amber-800 text-right bg-amber-50/50">
                  Thiếu Hụt Cần Mua
                </TableHead>
                <TableHead className="font-semibold text-slate-800 min-w-[160px]">
                  Nhà Cung Cấp
                </TableHead>
                <TableHead className="w-[100px] font-semibold text-slate-800 text-right">
                  Đơn Giá Dự Kiến
                </TableHead>
                <TableHead className="w-[110px] font-semibold text-slate-800 text-right">
                  Thành Tiền Dự Kiến
                </TableHead>
                <TableHead className="w-[100px] text-center font-semibold text-slate-900 sticky right-0 z-30 bg-slate-100 !bg-white border-l border-slate-200">
                  Thao Tác
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={12} className="h-32 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 mx-auto animate-spin mb-1 text-slate-400" />
                    Đang tải danh sách nhu cầu vật tư thiếu hụt...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={12}
                    className="h-32 text-center text-slate-500 bg-slate-50/30"
                  >
                    <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                    <p className="font-medium text-slate-700">
                      Tuyệt vời! Không có vật tư nào thiếu hụt trong các Lệnh SX hiện tại.
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Mọi nhu cầu nguyên vật liệu đã được cân đối đủ tồn kho hoặc đã lập Đơn Mua Hàng.
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item: any) => {
                  const isSelected = selectedPlanIds.has(item.id);
                  return (
                    <TableRow
                      key={item.id}
                      className={`hover:bg-slate-50/80 transition-colors ${
                        isSelected ? "bg-indigo-50/30" : ""
                      }`}
                    >
                      {/* Checkbox (Solid Sticky) */}
                      <TableCell className="w-10 px-2 py-1.5 text-center sticky left-0 z-10 bg-white !bg-white">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelectPlan(item.id)}
                          aria-label={`Chọn ${item.materialCode}`}
                        />
                      </TableCell>

                      {/* Mã Vật Tư (Solid Sticky) */}
                      <TableCell className="px-2 py-1.5 sticky left-10 z-10 bg-white !bg-white border-r border-slate-200">
                        {item.materialId ? (
                          <EntityLink
                            entityType="material"
                            id={item.materialId}
                            code={item.materialCode || "VT-UNKNOWN"}
                            search={{ returnUrl: "/purchasing?tab=shortage" }}
                          />
                        ) : (
                          <span className="font-mono font-semibold text-indigo-700 text-xs">
                            {item.materialCode || "VT-UNKNOWN"}
                          </span>
                        )}
                      </TableCell>

                      {/* Tên & Quy Cách */}
                      <TableCell className="px-2 py-1.5">
                        <div className="font-medium text-slate-900 leading-tight">
                          {item.materialName}
                        </div>
                        {item.specs && (
                          <div className="text-[11px] text-slate-500 truncate max-w-[260px]">
                            {item.specs}
                          </div>
                        )}
                      </TableCell>

                      {/* Lệnh SX F0 & Khách hàng */}
                      <TableCell className="px-2 py-1.5">
                        <div className="font-mono text-xs font-semibold text-slate-800">
                          {item.poId ? (
                            <EntityLink
                              entityType="productionOrder"
                              id={item.poId}
                              code={item.poCode}
                              search={{ returnUrl: "/purchasing?tab=shortage" }}
                            />
                          ) : (
                            item.poCode || "—"
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate max-w-[120px]">
                          {item.customerId ? (
                            <EntityLink
                              entityType="customer"
                              id={item.customerId}
                              label={item.customerName}
                              search={{ returnUrl: "/purchasing?tab=shortage" }}
                            />
                          ) : (
                            item.customerName || "—"
                          )}
                        </div>
                      </TableCell>

                      {/* Tồn Hiện Tại */}
                      <TableCell className="px-2 py-1.5 text-right font-mono text-slate-600 tabular-nums">
                        {formatNumber(item.currentStock)} {item.unit}
                      </TableCell>

                      {/* Nhu Cầu Kế Hoạch */}
                      <TableCell className="px-2 py-1.5 text-right font-mono text-slate-700 tabular-nums font-medium">
                        {formatNumber(item.qtyToOrder)} {item.unit}
                      </TableCell>

                      {/* Đã Đặt Mua */}
                      <TableCell className="px-2 py-1.5 text-right font-mono text-slate-500 tabular-nums">
                        {item.orderedQty > 0 ? (
                          <span className="text-blue-600 font-medium">
                            {formatNumber(item.orderedQty)}
                          </span>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      {/* Thiếu Hụt Cần Mua */}
                      <TableCell className="px-2 py-1.5 text-right font-mono font-bold text-amber-700 bg-amber-50/50 tabular-nums">
                        {formatNumber(item.shortageQty)} {item.unit}
                      </TableCell>

                      {/* Nhà Cung Cấp */}
                      <TableCell className="px-2 py-1.5">
                        <div className="font-medium text-slate-800 truncate max-w-[180px]">
                          {item.vendorId ? (
                            <EntityLink
                              entityType="vendor"
                              id={item.vendorId}
                              label={item.vendorName}
                              search={{ returnUrl: "/purchasing?tab=shortage" }}
                            />
                          ) : (
                            item.vendorName || "—"
                          )}
                        </div>
                        {item.vendorCode && (
                          <div className="text-[10px] text-slate-400 font-mono">
                            {item.vendorCode}
                          </div>
                        )}
                      </TableCell>

                      {/* Đơn Giá Dự Kiến */}
                      <TableCell className="px-2 py-1.5 text-right font-mono text-slate-600 tabular-nums">
                        {item.unitPrice > 0 ? formatMoney(item.unitPrice) : "-"}
                      </TableCell>

                      {/* Thành Tiền Dự Kiến */}
                      <TableCell className="px-2 py-1.5 text-right font-mono font-semibold text-slate-900 tabular-nums">
                        {item.estimatedAmount > 0
                          ? formatMoney(item.estimatedAmount)
                          : "-"}
                      </TableCell>

                      {/* Action (Solid Sticky) */}
                      <TableCell className="px-2 py-1 text-center sticky right-0 z-10 bg-white !bg-white border-l border-slate-200">
                        <Button
                          size="sm"
                          onClick={() => handleCreatePoSingle(item)}
                          className="h-6 px-2 text-[11px] bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 font-medium"
                          title="Lập Đơn Mua Hàng cho vật tư này"
                        >
                          <ShoppingCart className="w-3 h-3 mr-1 text-amber-600" />
                          Lập PO
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
    </EntityLinkContext.Provider>
  );
}
